import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  background,
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
  Image,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { useLoginUserMutation } from "store/auth/authApi";
import { loginSchema } from "utils/schema";
import HustleLogo from "../assets/images/Hustle.png";

function SignIn() {
  const [showPassword, setShowPassword] = useState(false);
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const [loginUser, { data, error, isLoading }] = useLoginUserMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const toast = useToast();

  const bodyBackground = useColorModeValue("white", "#0b0420");
  const textColor = useColorModeValue("brand.900", "brand.500");
  const placeholderColor = useColorModeValue("gray.500", "#9D8AC8");
  const borderColor = useColorModeValue("brand.200", "brand.200");
  const borderFocus = useColorModeValue("brand.400", "brand.400");
  const subCopy = useColorModeValue("gray.700", "gray.400"); // content text

  useEffect(() => {
    if (userInfo) navigate(from);
  }, [userInfo]);

  useEffect(() => {
    if (error) {
      const { message } = error;
      toast({
        position: "top-right",
        title: "Error",
        description: message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
    if (data?._id) navigate(from, { replace: true });
  }, [error, data]);

  return (
    <Box bg={bodyBackground} minH="100vh" color={textColor}>
      <Flex direction="column" align="center" pt={8} mb={4}>
        <Image
          src={HustleLogo}
          alt="Hustle Logo"
          width={["100px", "120px", "180px"]}
        />
      </Flex>

      <Formik
        validationSchema={loginSchema}
        initialValues={{ email: "", password: "" }}
        onSubmit={async (values) => {
          if (values) await loginUser(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isValid,
        }) => (
          <Flex
            justify="center"
            align="center"
            minH={["auto", "calc(100vh - 100px)"]}
            px={4}
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              w="100%"
              maxW="80%"
              align="center"
              justify="space-between"
              gap={10}
            >
              {/* Left Form */}
              <Flex
                direction="column"
                border="2x solid"
                borderRadius="30px"
                // bg="white"
                color={textColor}
                borderColor={borderColor}
                p={{ base: "24px", md: "50px" }}
                boxShadow={`10px 10px 0 ${textColor}`}
                flex="1"
              >
                <Text
                  fontSize="30px"
                  fontWeight="bold"
                  color={textColor}
                  mb={8}
                  textAlign="center"
                >
                  Let’s get you logged in
                </Text>

                <FormControl isRequired mb={6}>
                  <Input
                    size={"lg"}
                    type="email"
                    name="email"
                    placeholder="Email ID"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    isInvalid={touched.email && errors.email}
                    borderRadius="full"
                    fontSize="16px"
                    border={`2px solid ${borderColor}`}
                    color={textColor}
                    _placeholder={{ color: placeholderColor }}
                    _focus={{
                      borderColor: "#9b4dff",
                      boxShadow: "0 0 6px #9b4dff",
                    }}
                  />
                </FormControl>

                <FormControl isRequired mb={"20px"}>
                  <InputGroup>
                    <Input
                      size={"lg"}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      isInvalid={touched.password && errors.password}
                      borderRadius="full"
                      fontSize="16px"
                      border={`2px solid ${borderColor}`}
                      color={textColor}
                      _placeholder={{ color: placeholderColor }}
                      _focus={{
                        borderColor: "#9b4dff",
                        // boxShadow: "0 0 6px #9b4dff",
                      }}
                      _hover={{
                        background: "transparent",
                      }}
                    />
                    <InputRightElement h="full">
                      <Button
                        variant={"unstyled"}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <ViewIcon color="#9b4dff" />
                        ) : (
                          <ViewOffIcon color="#9b4dff" />
                        )}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Flex justify="flex-end" mb={"20px"}>
                  <Link
                    href="/forgot-password-change"
                    fontSize="14px"
                    color={subCopy}
                  >
                    Forgot password?
                  </Link>
                </Flex>

                <Button
                  type="submit"
                  w="full"
                  size={"lg"}
                  borderRadius="full"
                  bgClip="padding-box"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  colorScheme="brand"
                >
                  Sign in
                </Button>

                <Text mt={8} textAlign="center" fontSize="14px" color={subCopy}>
                  Don't have an account?
                  <Link
                    as={RouterLink}
                    to="/signUp"
                    fontWeight="bold"
                    ml={1}
                    color={textColor}
                  >
                    Sign up
                  </Link>
                </Text>
              </Flex>

              {/* Right Text */}
              <Box
                flex="1.5"
                textAlign={{ base: "center", md: "left" }}
                px={{ md: 6 }}
                display={{ base: "none", md: "block" }}
              >
                <Text
                  fontSize={{ base: "28px", md: "72px" }}
                  fontWeight="light"
                  color={textColor}
                  lineHeight="1.2"
                >
                  One smart move. One smart tool. Unlimited chill.
                </Text>
                {/* <Text
                  fontSize={{ base: "16px", md: "24px" }}
                  color={textColor}
                  fontWeight="medium"
                >
                  It’s giving performance and peace.
                </Text> */}
                <Text fontSize="80px">😎 🤖 🏖️</Text>
              </Box>
            </Flex>
          </Flex>
        )}
      </Formik>
    </Box>
  );
}

export default SignIn;